import React, { useCallback, useEffect, useState } from "react";
import axiosInstance from "../../../config/axiosApi/axiosApi";
import APIs from "../../../Constants/apis";
import Select from "react-select";
import DropDownStyles, { DropDownStylesDark } from "../../../pages/staff/dropdownStyles";

const CountryCitySelector = ({
  selectedCountry,
  selectedCity,
  setSelectedCity,
  onChange,
  setLoading,
  showCountryField = true,
  showCityField = true,
  cityLabel = "City",
  cityPlaceholder = "Select City",
  className,
  countryClassName,
  cityClassName,
  inlineLayout = false,
  selectedValueIsLabel = false,
  countryFieldName = "country",
  cityFieldName = "city",
  isDarkMode = false,
}) => {
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);

  // Define dropdown styles based on dark mode
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  const fetchCountries = useCallback(async () => {
    try {
      setLoading && setLoading(true);
      const response = await axiosInstance.get(APIs.GET_COUNTRIES);
      const countryData = response.data.data || response.data;

      setCountries(
        countryData.map((country) => ({
          value: country.code,
          label: country.name,
        }))
      );
    } catch (err) {
      // Error fetching countries silently
    } finally {
      setLoading && setLoading(false);
    }
  }, [setLoading]);

  const fetchCities = useCallback(
    async (countryCode) => {
      if (!countryCode) {
        setCities([]);
        return;
      }

      try {
        setLoading && setLoading(true);
        const response = await axiosInstance.get(APIs.GET_ALL_CITIES, {
          params: { CountryCode: countryCode },
        });
        const cityData = response.data.data || response.data;

        if (cityData) {
          const mappedCities = cityData.map((city) => ({
            value: selectedValueIsLabel ? city.cit_Name : city.cit_ID,
            label: city.cit_Name,
          }));
          setCities(mappedCities);
        } else {
          setCities([]);
        }
      } catch (err) {
        setCities([]);
      } finally {
        setLoading && setLoading(false);
      }
    },
    [setLoading, selectedValueIsLabel]
  );

  const handleCountryChange = (option) => {
    const countryCode = option?.value || "";

    if (onChange) {
      onChange({
        [countryFieldName]: countryCode,
        [cityFieldName]: "",
      });
    }

    if (countryCode) {
      fetchCities(countryCode);
    } else {
      setCities([]);
    }
  };

  const handleCityChange = (option, actionMeta) => {
    let cityValue = "";
    if (option) {
      if (actionMeta && actionMeta.action === "create-option") {
        cityValue = option.label;
      } else {
        cityValue = selectedValueIsLabel ? option.label : option.value;
      }
    }

    if (setSelectedCity) {
      setSelectedCity(cityValue);
    }

    if (onChange) {
      onChange({
        [countryFieldName]: selectedCountry,
        [cityFieldName]: cityValue,
      });
    }
  };

  useEffect(() => {
    fetchCountries();
  }, [fetchCountries]);

  useEffect(() => {
    if (selectedCountry) {
      fetchCities(selectedCountry);
    } else {
      setCities([]);
    }
  }, [selectedCountry, fetchCities]);

  const countryValue = countries.find((c) => c.value === selectedCountry) || null;

  const cityValue =
    selectedCity
      ? cities.find((c) =>
          selectedValueIsLabel ? c.label === selectedCity : c.value === selectedCity
        ) || { value: selectedCity, label: selectedCity }
      : null;

  const containerClass = inlineLayout ? className || "flex gap-4" : "";
  const countryContainerClass = inlineLayout ? (countryClassName || "flex-1") : "col-6";
  const cityContainerClass = inlineLayout ? (cityClassName || "flex-1") : (showCountryField ? "col-6" : "col-12");

  return (
    <div className={containerClass} style={inlineLayout ? { display: 'flex', gap: '16px', width: '100%' } : {}}>
      {showCountryField && (
        <div className={countryContainerClass} style={inlineLayout ? { flex: '1 1 0', minWidth: '0', maxWidth: '50%' } : {}}>
          <label className={`form-label ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>Country</label>
          <div className="input-group" style={{ width: '100%', display: 'flex' }}>
            <span 
              className={`input-group-text ${isDarkMode ? 'bg-dark text-white' : 'bg-light text-muted'}`}
              style={{ flexShrink: 0 }}
            >
              <i className="fa fa-globe"></i>
            </span>
            <div style={{ flex: '1', minWidth: '0', overflow: 'hidden' }}>
              <Select
                value={countryValue}
                onChange={handleCountryChange}
                options={countries}
                styles={dropdownStyles}
                placeholder="Select Country"
                isClearable={true}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>
          </div>
        </div>
      )}
      {showCityField && (
        <div className={cityContainerClass} style={inlineLayout ? { flex: '1 1 0', minWidth: '0', maxWidth: '50%' } : {}}>
          <label className={`form-label ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>{cityLabel}</label>
          <Select
            value={cityValue}
            onChange={handleCityChange}
            options={cities}
            styles={dropdownStyles}
            placeholder={cityPlaceholder}
            isClearable={true}
            isDisabled={!selectedCountry}
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        </div>
      )}
    </div>
  );
};

export default CountryCitySelector;